import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type PaymentUrlState = {
  q: string;
  status: string;
  billing_status: string;
  billing_type: string;
  fulfilled: string;
  uid: string;
  oid: string;
  ccode: string;
  date_from: string;
  date_to: string;
  amount_min: string;
  amount_max: string;
  sort_by: string;
  order: 'asc' | 'desc' | '';
  page: number;
  limit: number;
  pid: number | null;
  invid: number | null;
  tid: number | null;
  orderid: number | null;
  plid: number | null;
  bpmid: number | null;
  wid: number | null;
  eid: string;
  storeid: string;
};

const numOrNull = (v: string | null) => {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function usePaymentListUrlState(defaults?: Partial<PaymentUrlState>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<PaymentUrlState>(() => {
    const page = Math.max(1, Number(searchParams.get('page') || defaults?.page || 1) || 1);
    const limit = Math.min(200, Math.max(1, Number(searchParams.get('limit') || defaults?.limit || 20) || 20));
    const orderRaw = searchParams.get('order') || defaults?.order || '';
    const order = orderRaw === 'asc' || orderRaw === 'desc' ? orderRaw : '';
    return {
      q: searchParams.get('q') || defaults?.q || '',
      status: searchParams.get('status') || defaults?.status || '',
      billing_status: searchParams.get('billing_status') || defaults?.billing_status || '',
      billing_type: searchParams.get('billing_type') || defaults?.billing_type || '',
      fulfilled: searchParams.get('fulfilled') || defaults?.fulfilled || '',
      uid: searchParams.get('uid') || defaults?.uid || '',
      oid: searchParams.get('oid') || defaults?.oid || '',
      ccode: searchParams.get('ccode') || defaults?.ccode || '',
      date_from: searchParams.get('date_from') || defaults?.date_from || '',
      date_to: searchParams.get('date_to') || defaults?.date_to || '',
      amount_min: searchParams.get('amount_min') || defaults?.amount_min || '',
      amount_max: searchParams.get('amount_max') || defaults?.amount_max || '',
      sort_by: searchParams.get('sort_by') || defaults?.sort_by || '',
      order,
      page,
      limit,
      pid: numOrNull(searchParams.get('pid')),
      invid: numOrNull(searchParams.get('invid')),
      tid: numOrNull(searchParams.get('tid')),
      orderid: numOrNull(searchParams.get('orderid')),
      plid: numOrNull(searchParams.get('plid')),
      bpmid: numOrNull(searchParams.get('bpmid')),
      wid: numOrNull(searchParams.get('wid')),
      eid: searchParams.get('eid') || defaults?.eid || '',
      storeid: searchParams.get('storeid') || defaults?.storeid || '',
    };
  }, [searchParams, defaults]);

  const patch = useCallback(
    (updates: Partial<Record<keyof PaymentUrlState, string | number | null | undefined>>, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') next.delete(key);
            else next.set(key, String(value));
          });
          if (resetPage && !('page' in updates)) next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(
    (keep: Array<keyof PaymentUrlState> = []) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams();
          keep.forEach((k) => {
            const v = prev.get(k);
            if (v) next.set(k, v);
          });
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setDetail = useCallback(
    (key: 'pid' | 'invid' | 'tid' | 'orderid' | 'plid', id: number | null) => {
      patch({ [key]: id }, false);
    },
    [patch],
  );

  const toggleSort = useCallback(
    (field: string) => {
      if (state.sort_by === field) {
        patch({
          sort_by: field,
          order: state.order === 'asc' ? 'desc' : 'asc',
        });
      } else {
        patch({ sort_by: field, order: 'desc' });
      }
    },
    [patch, state.order, state.sort_by],
  );

  const offset = (state.page - 1) * state.limit;

  const toApiNumber = (v: string) => {
    if (!v.trim()) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    state,
    offset,
    patch,
    clearFilters,
    setDetail,
    toggleSort,
    toApiNumber,
  };
}
